import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
  type ReactElement,
} from 'react';
import styled from '@emotion/styled';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import SearchIcon from '@mui/icons-material/Search';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  Link,
  MenuItem,
  Paper,
  Popover,
  Select,
  type SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
// TODO: create chat query hints component in custom-pages-registry
import { CustomPageBrandContext, CustomPageUserContext } from '../../contexts/default.js';
import { useOptionalMslMcpProxyClient } from '../../contexts/msl-mcp-proxy.js';

// ============================================================================
// Types
// ============================================================================

type KolApiItem = Record<string, unknown>;

type HcpRow = {
  id: string;
  name: string;
  isKol: boolean;
  primaryAffiliation: string;
  location: string;
  email: string;
  phone: string | null;
};

type SortField = 'name' | 'primaryAffiliation' | 'location' | 'email' | 'phone';
type SortDirection = 'asc' | 'desc';

type FilterField = 'name' | 'primaryAffiliation' | 'location' | 'email' | 'phone';
type FilterOperator =
  | 'contains'
  | 'equals'
  | 'not equals'
  | 'starts with'
  | 'is empty'
  | 'is not empty';

type TableFilter = {
  id: string;
  field: FilterField;
  operator: FilterOperator;
  value: string;
};

type BackendFilterField = 'primaryAffiliation' | 'email' | 'phone';

type BackendHcpFilter = {
  field: BackendFilterField;
  operator: FilterOperator;
  value?: string;
};

// type RelatedQuestionHint = ComponentProps<typeof RelatedQuestionsPane>['hints'][number];

// ============================================================================
// Mock data
// ============================================================================

const MOCK_HCPS: HcpRow[] = [
  {
    id: '1',
    name: 'HCP Numero Uno',
    isKol: false,
    primaryAffiliation: 'Lincoln Behavorial Svc',
    location: '1111 W Outer Dr, Detroit, MI, 48235',
    email: 'numero1@lbsheath.ios',
    phone: '+1 (248) 3...',
  },
  {
    id: '2',
    name: 'HCP Numero Dos',
    isKol: true,
    primaryAffiliation: 'Iu Hlth Univ Hosp',
    location: '23423 University Blvd, Indianapolis, IN, 46202',
    email: 'numero2@iupui.edu',
    phone: '+1 (317) 2...',
  },
  {
    id: '3',
    name: 'HCP Numero Tres',
    isKol: false,
    primaryAffiliation: 'Taylor Health Ctr',
    location: '23432 Taylor Dr, Flint, MI, 48507',
    email: 'numero3@taylorlifecenter.org',
    phone: null,
  },
  {
    id: '4',
    name: 'HCP Numero Cuatro',
    isKol: false,
    primaryAffiliation: 'Stanton Hospital',
    location: '901 Memorial Rd, Birmingham, MI, 49931',
    email: 'numero4@nbhs.org',
    phone: '+1 (804) 8...',
  },
  {
    id: '5',
    name: 'HCP Numero Cinco',
    isKol: true,
    primaryAffiliation: 'HealthWest Health',
    location: '4343 E Apple Ave, Muskegon, MI, 49442',
    email: 'numero5@umich.edu',
    phone: '+1 (313) 3...',
  },
  {
    id: '6',
    name: 'HCP Numero Seis',
    isKol: false,
    primaryAffiliation: 'Oaklawn Health',
    location: '2324 Vista Dr, ...',
    email: 'numero6@pinesbhs.or...',
    phone: '...',
  },
];

// TODO: use the API proxy to get the questions from either the management api or MSL Agent

// TODO: let's start pulling some of these into msl utils to keep our component focused on display
const FIELD_LABELS: Record<FilterField, string> = {
  name: 'Name',
  primaryAffiliation: 'Primary Affiliation',
  location: 'Location',
  email: 'Email',
  phone: 'Phone',
};

const TEXT_OPERATORS: FilterOperator[] = [
  'contains',
  'equals',
  'not equals',
  'starts with',
  'is empty',
  'is not empty',
];
const SEARCH_DEBOUNCE_MS = 400;

const FILTER_FIELDS: FilterField[] = ['name', 'primaryAffiliation', 'location', 'email', 'phone'];

const requiresFilterValue = (operator: FilterOperator): boolean => {
  return operator !== 'is empty' && operator !== 'is not empty';
};

const getDefaultOperatorForField = (_field: FilterField): FilterOperator => {
  return 'contains';
};

const getFilterOperatorsForField = (_field: FilterField): FilterOperator[] => {
  return TEXT_OPERATORS;
};

const isBackendFilterField = (field: FilterField): field is BackendFilterField => {
  return field === 'primaryAffiliation' || field === 'email' || field === 'phone';
};

const toBackendHcpFilters = (allFilters: TableFilter[]): BackendHcpFilter[] => {
  const backendFilters: BackendHcpFilter[] = [];

  for (const filter of allFilters) {
    const field = filter.field;
    const operator = filter.operator;

    if (!isBackendFilterField(field)) {
      continue;
    }
    if (!isFilterValid(filter)) {
      continue;
    }

    if (!requiresFilterValue(operator)) {
      backendFilters.push({
        field,
        operator,
      });
      continue;
    }

    backendFilters.push({
      field,
      operator,
      value: filter.value.trim(),
    });
  }

  return backendFilters;
};

const isFilterValid = (filter: TableFilter): boolean => {
  if (!requiresFilterValue(filter.operator)) {
    return true;
  }
  return filter.value.trim().length > 0;
};

const buildFilterId = (): string => {
  const randomBuffer = new Uint32Array(1);
  crypto.getRandomValues(randomBuffer);
  return `${Date.now()}-${randomBuffer[0].toString(36)}`;
};
// ============================================================================
// Normaliser
// ============================================================================

const normalizeKolToHcpRow = (item: unknown): HcpRow | null => {
  if (!item || typeof item !== 'object') return null;
  const kol = item as KolApiItem;
  const firstAffiliation =
    Array.isArray(kol.affiliations) && kol.affiliations.length > 0
      ? (((kol.affiliations[0] as KolApiItem).name as string | undefined) ?? '')
      : '';
  const locationParts = [kol.streetAddress, kol.city, kol.state, kol.zipCode].filter(Boolean);
  return {
    id: String(kol.id ?? ''),
    name: String(kol.name ?? ''),
    isKol: kol.isKol === true,
    primaryAffiliation: firstAffiliation,
    location: locationParts.join(', '),
    email: String(kol.email ?? ''),
    phone: kol.phone ? String(kol.phone) : null,
  };
};

const normalizeHcpRows = (response: unknown): HcpRow[] => {
  if (!Array.isArray(response)) return [];
  return response.flatMap((item) => {
    const row = normalizeKolToHcpRow(item);
    return row ? [row] : [];
  });
};

// ============================================================================
// Component
// ============================================================================

export const MslTerritoryInsights = (): ReactElement => {
  const proxiesClient = useOptionalMslMcpProxyClient();
  const brand = useContext(CustomPageBrandContext);
  const user = useContext(CustomPageUserContext);
  const hasRendererContext = proxiesClient !== null && brand !== undefined;

  const [hcps, setHcps] = useState<HcpRow[]>();
  const [loading, setLoading] = useState(false);
  const [nameSearch, setNameSearch] = useState<string>('');
  const [addressSearch, setAddressSearch] = useState<string>('');
  const [sortBy, setSortBy] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filtersAnchorEl, setFiltersAnchorEl] = useState<HTMLElement | null>(null);
  const [filters, setFilters] = useState<TableFilter[]>([]);
  const [isTargetListHCP, setIsTargetListHCP] = useState<boolean>(true);
  const [isGlobalSearch, setIsGlobalSearch] = useState<boolean>(false);
  const backendFiltersRef = useRef<BackendHcpFilter[]>([]);

  useEffect(() => {
    backendFiltersRef.current = toBackendHcpFilters(filters);
  }, [filters]);

  const runSearch = useCallback(async () => {
    setPage(0);

    if (!hasRendererContext) {
      setHcps(MOCK_HCPS);
      return;
    }

    setLoading(true);
    try {
      const effectiveNameSearch = nameSearch.trim();

      const response = await proxiesClient.invokeMslMcpRoute('hcp-search', {
        brand_identifier: brand.identifier,
        ...(effectiveNameSearch ? { name: effectiveNameSearch } : {}),
        requesting_user_email: user?.email ?? '',
        territory_scope: isGlobalSearch ? 'all' : 'user',
        territory_insight_only: isTargetListHCP,
        ...(backendFiltersRef.current.length > 0 ? { hcp_filters: backendFiltersRef.current } : {}),
        size: 500,
      });
      const rows = normalizeHcpRows(response);
      setHcps(rows);
    } catch (err) {
      setHcps([]);
      console.error('Failed to search HCPs', err);
    } finally {
      setLoading(false);
    }
  }, [brand, hasRendererContext, proxiesClient, isGlobalSearch, isTargetListHCP, user, nameSearch]);

  const handleSearch = useCallback(async () => {
    await runSearch();
  }, [nameSearch, runSearch]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      runSearch().catch((error) => {
        console.error('Failed to load territory HCPs', error);
      });
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [runSearch, isGlobalSearch, isTargetListHCP]);

  const handleSort = useCallback(
    (field: SortField) => {
      if (sortBy === field) {
        setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortBy(field);
        setSortDirection('asc');
      }
      setPage(0);
    },
    [sortBy]
  );

  const filteredHcps = useMemo(() => {
    if (!hcps) {
      return [];
    }

    return hcps.filter((row) => filters.every((filter) => doesRowMatchFilter(row, filter)));
  }, [filters, hcps]);

  const sortedHcps = useMemo(() => {
    const rows = [...filteredHcps];
    rows.sort((left, right) => {
      const leftValue = getSortValue(left, sortBy).toLowerCase();
      const rightValue = getSortValue(right, sortBy).toLowerCase();
      const comparison = leftValue.localeCompare(rightValue);
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return rows;
  }, [filteredHcps, sortBy, sortDirection]);

  const paginatedHcps = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedHcps.slice(start, start + rowsPerPage);
  }, [page, rowsPerPage, sortedHcps]);

  const hasActiveFilters = useMemo(() => {
    return filters.some((filter) => isFilterValid(filter));
  }, [filters]);

  const addFilter = useCallback((field: FilterField) => {
    setFilters((current) => {
      if (current.length >= 3) {
        return current;
      }

      return [
        ...current,
        {
          id: buildFilterId(),
          field,
          operator: getDefaultOperatorForField(field),
          value: '',
        },
      ];
    });
    setPage(0);
  }, []);

  const updateFilter = useCallback((id: string, update: Partial<TableFilter>) => {
    setFilters((current) => {
      return current.map((filter) => {
        if (filter.id !== id) {
          return filter;
        }

        const nextField = update.field ?? filter.field;
        const nextOperator = update.operator ?? filter.operator;
        const nextValue = (() => {
          if (update.value !== undefined) {
            return update.value;
          }
          if (update.field !== undefined) {
            return '';
          }
          if (!requiresFilterValue(nextOperator)) {
            return '';
          }
          return filter.value;
        })();

        return {
          ...filter,
          ...update,
          field: nextField,
          operator: nextOperator,
          value: nextValue,
        };
      });
    });
    setPage(0);
  }, []);

  const removeFilter = useCallback((id: string) => {
    setFilters((current) => current.filter((filter) => filter.id !== id));
    setPage(0);
  }, []);

  const openFiltersForField = useCallback((event: MouseEvent<HTMLElement>, field: FilterField) => {
    event.preventDefault();
    event.stopPropagation();

    setFiltersAnchorEl(event.currentTarget);
    setFilters((current) => {
      if (current.some((filter) => filter.field === field) || current.length >= 3) {
        return current;
      }

      return [
        ...current,
        {
          id: buildFilterId(),
          field,
          operator: getDefaultOperatorForField(field),
          value: '',
        },
      ];
    });
  }, []);

  const closeFilters = useCallback(() => {
    setFiltersAnchorEl(null);
  }, []);

  const isTerritoryTargetCheckbox = <Checkbox
            checked={isTargetListHCP}
            onChange={() => setIsTargetListHCP(!isTargetListHCP)}
            slotProps={{
              input: { 'aria-label': 'Only Target HCPs' },
            }}
          />;

const isGlobalSearchCheckbox = <Checkbox
            checked={isGlobalSearch}
            onChange={() => setIsGlobalSearch(!isGlobalSearch)}
            slotProps={{
              input: { 'aria-label': 'Global Search' },
            }}
          />;

  return (
    <Root>
      <Stack spacing={2}>
      <Paper elevation={0} sx={{ p: 2, maxWidth: '90vw', borderRadius: '5px'}}>
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2, rowGap: 1, columnGap: 2 }}
        >
          <Typography variant="body2" sx={{ mb: 0 }}>
            These are the potential HCPs that you can engage with in your territory.
          </Typography>
          <FormGroup row sx={{ gap: 2 }}>
            <FormControlLabel
              control={isTerritoryTargetCheckbox}
              label="Only Target HCPs"
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
            />
            <FormControlLabel
              control={isGlobalSearchCheckbox}
              label="Global Search"
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
            />
          </FormGroup>
        </Grid>

        {/* HCP Table */}
        <Box sx={{ position: 'relative' }}>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 0 }}>
            <Table
              size="small"
              sx={{
                tableLayout: 'fixed',
                '& thead, & tbody tr': {
                  display: 'table',
                  width: '100%',
                  tableLayout: 'fixed',
                },
                '& tbody': {
                  display: 'block',
                  height: '50vh',
                  overflowY: 'auto',
                },
              }}
            >
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <SortableHeader
                    field="name"
                    label="Name"
                    onSort={handleSort}
                    sortBy={sortBy}
                    sortDirection={sortDirection}
                    hasActiveFilter={filters.some((filter) => filter.field === 'name' && isFilterValid(filter))}
                    onOpenFilters={openFiltersForField}
                  />
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Profile</TableCell>
                  <SortableHeader
                    field="primaryAffiliation"
                    label="Primary Affiliation"
                    onSort={handleSort}
                    sortBy={sortBy}
                    sortDirection={sortDirection}
                    hasActiveFilter={filters.some(
                      (filter) => filter.field === 'primaryAffiliation' && isFilterValid(filter)
                    )}
                    onOpenFilters={openFiltersForField}
                  />
                  <SortableHeader
                    field="location"
                    label="Location"
                    onSort={handleSort}
                    sortBy={sortBy}
                    sortDirection={sortDirection}
                    hasActiveFilter={filters.some(
                      (filter) => filter.field === 'location' && isFilterValid(filter)
                    )}
                    onOpenFilters={openFiltersForField}
                  />
                  <SortableHeader
                    field="email"
                    label="Email"
                    onSort={handleSort}
                    sortBy={sortBy}
                    sortDirection={sortDirection}
                    hasActiveFilter={filters.some(
                      (filter) => filter.field === 'email' && isFilterValid(filter)
                    )}
                    onOpenFilters={openFiltersForField}
                  />
                  <SortableHeader
                    field="phone"
                    label="Phone"
                    onSort={handleSort}
                    sortBy={sortBy}
                    sortDirection={sortDirection}
                    hasActiveFilter={filters.some(
                      (filter) => filter.field === 'phone' && isFilterValid(filter)
                    )}
                    onOpenFilters={openFiltersForField}
                  />
                </TableRow>
              </TableHead>

              <TableBody>
                {paginatedHcps.length > 0 ? (
                  paginatedHcps.map((hcp) => (
                    <TableRow key={hcp.id} hover>
                      <TableCell sx={{ fontSize: '0.8rem' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {hcp.name}
                          {hcp.isKol && <KolBadge>KOL</KolBadge>}
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Button
                          size="small"
                          variant="outlined"
                          sx={{
                            fontSize: '0.7rem',
                            borderColor: '#28aae1',
                            color: '#28aae1',
                            textTransform: 'none',
                            borderRadius: '16px',
                            px: 1.5,
                            py: 0.25,
                            '&:hover': { borderColor: '#2691ca', color: '#2691ca' },
                          }}
                        >
                          → View
                        </Button>
                      </TableCell>

                      <TableCell sx={{ fontSize: '0.8rem' }}>{hcp.primaryAffiliation}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{hcp.location}</TableCell>

                      <TableCell sx={{ fontSize: '0.8rem', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                        {hcp.email ? (
                          <Link
                            href={`mailto:${hcp.email}`}
                            underline="hover"
                            color="inherit"
                            sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                          >
                            {hcp.email}
                          </Link>
                        ) : null}
                      </TableCell>

                      <TableCell sx={{ fontSize: '0.8rem', color: hcp.phone ? 'inherit' : '#aaa' }}>
                        {hcp.phone ?? 'None'}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                      {!loading ? (
                        <Typography variant="body2" color="text.secondary">
                          No HCPs found.
                        </Typography>
                      ) : null}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

          </TableContainer>

          {loading ? (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.55)',
                zIndex: 2,
              }}
            >
              <CircularProgress size={28} />
            </Box>
          ) : null}
        </Box>

        <TablePagination
          component="div"
          count={sortedHcps.length}
          page={page}
          onPageChange={(_event, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25]}
          labelRowsPerPage="Rows per page:"
        />

        <Popover
          open={filtersAnchorEl !== null}
          anchorEl={filtersAnchorEl}
          onClose={closeFilters}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          <FilterPopoverContainer>
            {filters.map((filter) => {
              const availableOperators = getFilterOperatorsForField(filter.field);
              const operatorNeedsValue = requiresFilterValue(filter.operator);

              return (
                <FilterRow key={filter.id}>
                  <Select
                    size="small"
                    value={filter.field}
                    onChange={(event: SelectChangeEvent<FilterField>) => {
                      const nextField = event.target.value;
                      updateFilter(filter.id, {
                        field: nextField,
                        operator: getDefaultOperatorForField(nextField),
                        value: '',
                      });
                    }}
                    sx={{ minWidth: 180 }}
                  >
                    {FILTER_FIELDS.map((field) => (
                      <MenuItem key={field} value={field}>
                        {FIELD_LABELS[field]}
                      </MenuItem>
                    ))}
                  </Select>

                  <Select
                    size="small"
                    value={filter.operator}
                    onChange={(event: SelectChangeEvent<FilterOperator>) => {
                      const nextOperator = event.target.value;
                      updateFilter(filter.id, {
                        operator: nextOperator,
                        value: requiresFilterValue(nextOperator) ? filter.value : '',
                      });
                    }}
                    sx={{ minWidth: 180 }}
                  >
                    {availableOperators.map((operator) => (
                      <MenuItem key={operator} value={operator}>
                        {operator}
                      </MenuItem>
                    ))}
                  </Select>

                  <TextField
                    size="small"
                    value={filter.value}
                    placeholder="Filter value"
                    disabled={!operatorNeedsValue}
                    onChange={(event) => {
                      updateFilter(filter.id, { value: event.target.value });
                    }}
                    sx={{ minWidth: 220 }}
                  />

                  <IconButton
                    size="small"
                    onClick={() => {
                      removeFilter(filter.id);
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>

                  {filters.length < 3 && (
                    <IconButton
                      size="small"
                      sx={{ color: '#28aae1' }}
                      onClick={() => {
                        addFilter('name');
                      }}
                    >
                      <AddCircleIcon fontSize="small" />
                    </IconButton>
                  )}
                </FilterRow>
              );
            })}

            {filters.length === 0 && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  addFilter('name');
                }}
                sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
              >
                Add filter
              </Button>
            )}

            {hasActiveFilters && (
              <FilterHintText>Filtering is applied instantly to table rows.</FilterHintText>
            )}

            {filters.length >= 3 && <FilterHintText>Maximum of 3 filters allowed.</FilterHintText>}
          </FilterPopoverContainer>
        </Popover>



      <Box sx={{mx: 'auto'}}>
        {/* HCP Search Section */}
        <Typography variant="body2" sx={{ mb: 2 }}>
          Or if you're looking for a different HCP, get started with an HCP search.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, maxWidth: 420 }}>
          <Typography variant="body2" fontWeight={600}>
            Search by Name
          </Typography>

          <Autocomplete
            freeSolo
            options={[]}
            inputValue={nameSearch}
            onInputChange={(_e, v) => setNameSearch(v)}
            renderInput={(params) => (
              <TextField {...params} placeholder="Name" size="small" variant="outlined" />
            )}
          />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 0.5 }}>
            <Divider sx={{ flex: 1 }} />
            <Typography variant="body2" color="text.secondary">
              OR
            </Typography>
            <Divider sx={{ flex: 1 }} />
          </Box>

          <Typography variant="body2" fontWeight={600}>
            Search by address or current location
          </Typography>

          <Autocomplete
            freeSolo
            options={[]}
            inputValue={addressSearch}
            onInputChange={(_e, v) => setAddressSearch(v)}
            renderInput={(params) => (
              <TextField {...params} placeholder="Start typing an address" size="small" variant="outlined" />
            )}
          />

          <Link
            component="button"
            variant="body2"
            underline="hover"
            sx={{ color: '#28aae1', display: 'flex', alignItems: 'center', gap: 0.5, width: 'fit-content' }}
          >
            <GpsFixedIcon sx={{ fontSize: 14 }} />
            Share my current location
          </Link>

          <Button
            variant="outlined"
            size="small"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={14} /> : <SearchIcon />}
            onClick={() => {
              handleSearch().catch((error) => {
                console.error('Failed to search HCPs', error);
              });
            }}
            sx={{
              textTransform: 'none',
              color: 'text.secondary',
              borderColor: '#ccc',
              width: 'fit-content',
              fontSize: '0.8rem',
            }}
          >
            Search for profiles.
          </Button>
        </Box>
      </Box>
      </Paper>
      </Stack>
    </Root>
  );
};

// ============================================================================
// Sub-components
// ============================================================================

const SortableHeader = ({
  field,
  label,
  onSort,
  sortBy,
  sortDirection,
  hasActiveFilter,
  onOpenFilters,
}: {
  field: SortField;
  label: string;
  onSort: (field: SortField) => void;
  sortBy: SortField;
  sortDirection: SortDirection;
  hasActiveFilter: boolean;
  onOpenFilters: (event: MouseEvent<HTMLElement>, field: FilterField) => void;
}): ReactElement => (
  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.5 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
      <TableSortLabel
        active={sortBy === field}
        direction={sortBy === field ? sortDirection : 'asc'}
        onClick={() => onSort(field)}
        sx={{ color: 'inherit', '& .MuiTableSortLabel-icon': { opacity: 1 } }}
      >
        {label}
      </TableSortLabel>
      <IconButton
        aria-label={`Filter ${label}`}
        size="small"
        onClick={(event) => {
          onOpenFilters(event, field);
        }}
      >
        <FilterListIcon
          sx={{
            fontSize: 16,
            color: hasActiveFilter ? '#28aae1' : 'rgba(0,0,0,0.64)',
          }}
        />
      </IconButton>
    </Box>
  </TableCell>
);

const getSortValue = (row: HcpRow, field: SortField): string => {
  switch (field) {
    case 'name':
      return row.name;
    case 'primaryAffiliation':
      return row.primaryAffiliation;
    case 'location':
      return row.location;
    case 'email':
      return row.email;
    case 'phone':
      return row.phone ?? '';
    default:
      return row.name;
  }
};

const getFilterValue = (row: HcpRow, field: FilterField): string => {
  switch (field) {
    case 'name':
      return row.name;
    case 'primaryAffiliation':
      return row.primaryAffiliation;
    case 'location':
      return row.location;
    case 'email':
      return row.email;
    case 'phone':
      return row.phone ?? '';
    default:
      return '';
  }
};

const doesRowMatchFilter = (row: HcpRow, filter: TableFilter): boolean => {
  if (!isFilterValid(filter)) {
    return true;
  }

  const fieldValue = getFilterValue(row, filter.field).trim().toLowerCase();
  const filterValue = filter.value.trim().toLowerCase();

  switch (filter.operator) {
    case 'contains':
      return fieldValue.includes(filterValue);
    case 'equals':
      return fieldValue === filterValue;
    case 'not equals':
      return fieldValue !== filterValue;
    case 'starts with':
      return fieldValue.startsWith(filterValue);
    case 'is empty':
      return fieldValue.length === 0;
    case 'is not empty':
      return fieldValue.length > 0;
    default:
      return true;
  }
};

// ============================================================================
// Styled
// ============================================================================

const Root = styled.div({
  padding: '24px',
  boxSizing: 'border-box',
  width: '100%',
});

const KolBadge = styled.span({
  fontSize: '0.65rem',
  fontWeight: 600,
  color: '#28aae1',
  lineHeight: 1,
});

const FilterPopoverContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  padding: 12,
  minWidth: 780,
  backgroundColor: '#fff',
  borderRadius: 6,
  boxSizing: 'border-box',
});

const FilterRow = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
});

const FilterHintText = styled.div({
  fontSize: '0.75rem',
  color: '#666',
});
